/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks/set-state-in-effect */
/**
 * AssumedRoleContext — Admin "Switch Role" feature
 *
 * Allows admins to assume any tenant role to preview the app as that role sees it.
 * The assumed role is:
 * - Stored in localStorage (survives refresh, per-tenant scoped)
 * - Sent as X-Assume-Role-Id header on every API request
 * - Used by useCanAccess to evaluate frontend permissions
 * - Displayed as a colored chip in the header
 *
 * Design follows patterns from AWS IAM Switch Role, Google Cloud "Test as",
 * and Okta admin impersonation.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  type ReactNode,
} from 'react'
import { useAuth } from './AuthContext'
import { useTenant } from './TenantContext'
import { request } from '../services/api/core'
import { logger } from '../utils/logger'

const STORAGE_KEY_PREFIX = 'tagp-assumed-role'

function storageKey(tenantId: string | null): string {
  return tenantId ? `${STORAGE_KEY_PREFIX}-${tenantId}` : STORAGE_KEY_PREFIX
}

/**
 * platform_admin is a privilege (granted by role OR by customer-config email),
 * not an assumable permission set. Assuming it strips the platform-admin bypass
 * and evaluates the role's non-standard permissions, locking the admin out of the
 * UI. We never hold such an assumption — this is the single chokepoint that also
 * self-heals stale localStorage written by older clients.
 */
function isPlatformAdminRole(name: string | undefined | null): boolean {
  return (name ?? '').toLowerCase().replace(/[\s-]/g, '_') === 'platform_admin'
}

export interface AssumedRoleInfo {
  id: string
  name: string
  description: string | null
  permissions: Record<string, unknown> | null
  /** Scoped policy documents (e.g. skills-feature-access). */
  attached_policies?: Array<{ service: string; actions: Record<string, boolean> }> | null
}

export interface SwitchableRole {
  id: string
  name: string
  description: string | null
  is_own_role: boolean
}

interface AssumedRoleContextValue {
  assumedRole: AssumedRoleInfo | null
  isAssuming: boolean
  canSwitchRoles: boolean
  assumeRole: (roleId: string) => Promise<void>
  clearAssumedRole: () => void
  fetchSwitchableRoles: () => Promise<SwitchableRole[]>
}

const AssumedRoleContext = createContext<AssumedRoleContextValue | undefined>(undefined)

export const AssumedRoleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentUser, notePrivilegeChange } = useAuth()
  const { isPlatformAdmin, currentTenant } = useTenant()
  const [assumedRole, setAssumedRole] = useState<AssumedRoleInfo | null>(null)
  const [isAssuming, setIsAssuming] = useState(false)

  const isAdmin = useMemo(() => {
    if (isPlatformAdmin) return true
    if (!currentUser?.roles) return false
    const adminRoles = new Set(['admin', 'platform admin', 'platform_admin'])
    return currentUser.roles.some((r) => adminRoles.has(r.name.toLowerCase()))
  }, [currentUser, isPlatformAdmin])

  // Restore assumed role from localStorage on mount / tenant change
  useEffect(() => {
    if (!isAdmin || !currentUser) {
      setAssumedRole(null)
      return
    }
    try {
      const key = storageKey(currentTenant?.id ?? null)
      const stored = localStorage.getItem(key)
      if (stored) {
        const parsed = JSON.parse(stored) as AssumedRoleInfo
        // Drop a stale platform_admin assumption (older clients allowed it) so the
        // real privilege is restored instead of being stripped on every reload.
        if (isPlatformAdminRole(parsed.name)) {
          localStorage.removeItem(key)
          setAssumedRole(null)
        } else {
          setAssumedRole(parsed)
        }
      } else {
        setAssumedRole(null)
      }
    } catch {
      setAssumedRole(null)
    }
  }, [isAdmin, currentUser, currentTenant?.id])

  const clearAssumedRole = useCallback(() => {
    const key = storageKey(currentTenant?.id ?? null)
    let hadStored = false
    let removeFailed = false
    try {
      hadStored = localStorage.getItem(key) !== null
      if (hadStored) localStorage.removeItem(key)
    } catch {
      removeFailed = true
    }
    if (!assumedRole && !hadStored) return
    setAssumedRole(null)
    if (removeFailed) return
    notePrivilegeChange()
  }, [assumedRole, currentTenant?.id, notePrivilegeChange])

  const assumeRole = useCallback(
    async (roleId: string) => {
      setIsAssuming(true)
      try {
        // Defensive: assuming platform_admin would strip the privilege. The modal
        // already routes this to a revert, but guard the context too so no caller
        // can put the admin into the broken state.
        const result = await request<{
          role_id: string
          role_name: string
          role_description: string | null
          permissions: Record<string, unknown> | null
          attached_policies?: Array<{ service: string; actions: Record<string, boolean> }> | null
        }>('/auth/assume-role', {
          method: 'POST',
          body: JSON.stringify({ role_id: roleId }),
        })

        // platform_admin is a privilege, not an assumable role — clear instead of
        // assume so the admin keeps full access (matches SwitchRoleModal's revert).
        if (isPlatformAdminRole(result.role_name)) {
          clearAssumedRole()
          return
        }

        const info: AssumedRoleInfo = {
          id: result.role_id,
          name: result.role_name,
          description: result.role_description,
          permissions: result.permissions,
          // Scoped grants (skills, agent-studio, ideate, evals, …) live here.
          // Without them the role looks "legacy" to useCanAccess and the UI
          // grants access the backend then 403s — making the preview diverge
          // from a real login.
          attached_policies: result.attached_policies ?? null,
        }

        setAssumedRole(info)

        try {
          const key = storageKey(currentTenant?.id ?? null)
          localStorage.setItem(key, JSON.stringify(info))
        } catch {
          logger.warn('Failed to persist assumed role')
          return
        }
        notePrivilegeChange()
      } finally {
        setIsAssuming(false)
      }
    },
    [clearAssumedRole, currentTenant?.id, notePrivilegeChange]
  )

  useEffect(() => {
    const key = storageKey(currentTenant?.id ?? null)
    const onStorage = (event: StorageEvent) => {
      if (event.key !== key) return
      if (!event.newValue) {
        setAssumedRole(null)
        notePrivilegeChange()
        return
      }
      try {
        const parsed = JSON.parse(event.newValue) as AssumedRoleInfo
        setAssumedRole(isPlatformAdminRole(parsed.name) ? null : parsed)
      } catch {
        setAssumedRole(null)
      }
      notePrivilegeChange()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [currentTenant?.id, notePrivilegeChange])

  const fetchSwitchableRoles = useCallback(async (): Promise<SwitchableRole[]> => {
    return request<SwitchableRole[]>('/auth/roles/switchable')
  }, [])

  const contextValue = useMemo<AssumedRoleContextValue>(
    () => ({
      assumedRole,
      isAssuming,
      canSwitchRoles: isAdmin,
      assumeRole,
      clearAssumedRole,
      fetchSwitchableRoles,
    }),
    [assumedRole, isAssuming, isAdmin, assumeRole, clearAssumedRole, fetchSwitchableRoles]
  )

  return (
    <AssumedRoleContext.Provider value={contextValue}>{children}</AssumedRoleContext.Provider>
  )
}

export function useAssumedRole(): AssumedRoleContextValue {
  const context = useContext(AssumedRoleContext)
  if (context === undefined) {
    throw new Error('useAssumedRole must be used within an AssumedRoleProvider')
  }
  return context
}
